import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActiveTripSummary, NextTripSummary } from '../../services/plannerApi';
import DaylightLine from './DaylightLine';
import { Badge } from '../common/Badge';
import './DepartureBoard.css';

/**
 * The between-trips state on Now (spec §22.5): "a quiet departure board".
 *
 * Quiet is the requirement, not a style note. Between trips there is nothing
 * urgent to say, and a surface that invents urgency — a countdown pulsing at 94
 * days, a prompt to connect a calendar — trains people to ignore it. So this
 * states the next departure and stops.
 *
 * Deliberately absent: any calendar prompt. Phase 4 freezes calendar
 * acquisition, and prompts live in Settings only.
 *
 * The departure-board idiom follows #56: the time anchors a mono left column,
 * emphasis comes from the type scale, and there is no tinted panel with an
 * accent rail — the shape almost every generated card takes.
 *
 * Today's daylight line (Almanac S3, D6) sits above it in every state; it draws
 * nothing without a sun location. The countdown is an ink outline badge (D4:
 * gold is the SUNSET tag's alone).
 */

interface DepartureBoardProps {
  nextTrip: NextTripSummary | null;
  activeTrip?: ActiveTripSummary | null;
}

function formatDeparture(isoDate: string): string {
  // Parsed as local parts, not `new Date(iso)` — that reads a bare date as UTC
  // and shows the previous day for anyone west of Greenwich.
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export const DepartureBoard: React.FC<DepartureBoardProps> = ({ nextTrip, activeTrip = null }) => {
  const navigate = useNavigate();

  if (activeTrip) {
    return (
      <>
      <DaylightLine />
      <section className="departure-board departure-board--active" aria-label="Active trip">
        <button type="button" className="departure-board__active" onClick={() => navigate(`/itinerary/${activeTrip.id}`)}>
          <span className="departure-board__active-kicker">YOU'RE IN</span>
          <strong>{activeTrip.destination || activeTrip.title}</strong>
          <span>Day {activeTrip.day_number} of {activeTrip.total_days}</span>
        </button>
      </section>
      </>
    );
  }

  if (!nextTrip) {
    return (
      <>
      <DaylightLine />
      <section className="departure-board" aria-label="Next departure">
        <p className="departure-board__empty">No trips planned.</p>
        <button
          type="button"
          className="departure-board__cta"
          onClick={() => navigate('/explore')}
        >
          Find somewhere to go
        </button>
      </section>
      </>
    );
  }

  const { days_until: daysUntil } = nextTrip;

  return (
    <>
    <DaylightLine />
    <section className="departure-board" aria-label="Next departure">
      <ol className="departure-board__rows">
        <li className="departure-board__row">
          <span className="departure-board__when">
            {formatDeparture(nextTrip.start_date)}
          </span>
          <span className="departure-board__rule" aria-hidden="true" />
          <button
            type="button"
            className="departure-board__where"
            onClick={() => navigate(`/itinerary/${nextTrip.id}`)}
          >
            <span className="departure-board__destination">
              {nextTrip.destination || nextTrip.title}
            </span>
            <Badge className="departure-board__countdown">
              {daysUntil === 1 ? 'IN 1 DAY' : `IN ${daysUntil} DAYS`}
            </Badge>
          </button>
        </li>
      </ol>
    </section>
    </>
  );
};

export default DepartureBoard;
